import {
  Component,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { StateMagmntService } from '../../shared/services/state-magmnt-service';

@Component({
  selector: 'app-common-loader',
  standalone: true,
  imports: [NgxSpinnerModule],
  templateUrl: './common-loader.component.html',
  styleUrls: ['./common-loader.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CommonLoaderComponent implements OnDestroy {
  private spinner = inject(NgxSpinnerService);
  private stateMagmntService = inject(StateMagmntService);
  private elementRef = inject(ElementRef<HTMLElement>);
  private topLayerObserver?: MutationObserver;

  constructor() {
    effect(() => {
      const isLoading = this.stateMagmntService.showLoader();
      if (isLoading) {
        this.spinner.show();
        queueMicrotask(() => this.syncTopLayer());
        setTimeout(() => this.syncTopLayer(), 0);
      } else {
        this.spinner.hide();
      }
    });

    /* Promote the spinner to the browser's top layer when a CDK overlay/dialog is open */
    this.topLayerObserver = new MutationObserver(() => this.syncTopLayer());
    this.topLayerObserver.observe(this.elementRef.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  ngOnDestroy(): void {
    this.topLayerObserver?.disconnect();
  }

  private syncTopLayer(): void {
    const overlay: (HTMLElement & { showPopover?: () => void }) | null =
      this.elementRef.nativeElement.querySelector('.ngx-spinner-overlay');
    if (!overlay) return;

    const isDialogOpen = this.isCdkOverlayOpen();
    if (!isDialogOpen) {
      if (overlay.hasAttribute('popover')) {
        overlay.removeAttribute('popover');
      }
      return;
    }

    if (typeof overlay.showPopover !== 'function') return;

    if (!overlay.hasAttribute('popover')) {
      overlay.setAttribute('popover', 'manual');
    }

    if (!overlay.matches(':popover-open')) {
      try {
        overlay.showPopover();
      } catch {
        // Element connected/open safety check
      }
    }
  }

  private isCdkOverlayOpen(): boolean {
    const overlayContainer = document.querySelector('.cdk-overlay-container');
    return !!overlayContainer && overlayContainer.childElementCount > 0;
  }
}
